import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Save, Hourglass } from "lucide-react";

const empty = {
  stock_id: "",
  qty_expected: "",
  eta: "",
  supplier_id: "",
  po_ref: "",
  notes: "",
};

/**
 * Manager-only — create or edit a backorder.
 * `lockedStockId` pre-fills + locks the stock picker (used when launching
 * from a Catalog card).
 */
const BackorderFormDialog = ({
  open,
  onClose,
  editing,
  lockedStockId,
  onSaved,
}) => {
  const [form, setForm] = useState(empty);
  const [stocks, setStocks] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(
      editing
        ? {
            ...empty,
            ...editing,
            qty_expected: editing.qty_expected ?? "",
            eta: (editing.eta || "").slice(0, 10),
            supplier_id: editing.supplier_id || "",
            po_ref: editing.po_ref || "",
            notes: editing.notes || "",
          }
        : { ...empty, stock_id: lockedStockId || "" },
    );
    Promise.all([api.get("/stock"), api.get("/suppliers")])
      .then(([s, sp]) => {
        setStocks(s.data || []);
        setSuppliers(sp.data || []);
      })
      .catch(() => {});
  }, [open, editing, lockedStockId]);

  // When the stocks list arrives (or the dialog is opened locked to a
  // specific stock), copy that stock's default supplier into the form
  // if the user hasn't already picked one. Handles the flow where
  // BackorderFormDialog is opened straight from a Catalog card via
  // `lockedStockId` — without this the Supplier dropdown would stay
  // blank until the user clicked into it.
  useEffect(() => {
    if (!open || editing) return;
    if (!stocks.length || !form.stock_id || form.supplier_id) return;
    const s = stocks.find((x) => x.id === form.stock_id);
    if (s?.supplier_id) {
      setForm((f) => ({ ...f, supplier_id: s.supplier_id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stocks, open]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // When the user picks (or changes) the stock item, auto-fill Supplier
  // from that item's default supplier — saves a second dropdown hunt in
  // the common case where the supplier is who they're chasing the
  // backorder from. Won't clobber a supplier the user has already
  // picked explicitly (drop only fires when supplier_id is empty OR
  // it still matches the previous stock's default).
  const pickStock = (newId) => {
    setForm((f) => {
      const prev = stocks.find((s) => s.id === f.stock_id);
      const next = stocks.find((s) => s.id === newId);
      const userTouched = f.supplier_id && f.supplier_id !== prev?.supplier_id;
      return {
        ...f,
        stock_id: newId,
        supplier_id: userTouched
          ? f.supplier_id
          : next?.supplier_id || "",
      };
    });
  };

  const submit = async () => {
    if (!form.stock_id) return toast.error("Pick an item");
    if (!form.qty_expected || Number(form.qty_expected) <= 0)
      return toast.error("Qty must be > 0");
    setBusy(true);
    try {
      const payload = {
        ...form,
        qty_expected: Number(form.qty_expected),
        eta: form.eta || null,
        supplier_id: form.supplier_id || null,
      };
      if (editing) {
        await api.put(`/backorders/${editing.id}`, payload);
        toast.success("Backorder updated");
      } else {
        await api.post("/backorders", payload);
        toast.success("Backorder flagged");
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  // Render into document.body via a portal so the modal escapes any
  // ancestor with `backdrop-filter`, `transform`, `filter` or `perspective`
  // — all of which create a containing block that traps `position: fixed`
  // and makes the modal render as a small strip inside the parent card
  // (user report Aug 27 2026: Backorder dialog sitting behind Low Stock
  // was actually the modal being rendered inside a Dashboard .nb-card
  // whose backdrop-filter broke fixed positioning).
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="backorder-form-dialog"
      onClick={onClose}
    >
      <div
        className="nb-card w-[min(640px,94vw)] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b-2 border-black flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Backorders</div>
            <h3 className="font-display text-2xl flex items-center gap-2">
              <Hourglass size={20} strokeWidth={2.5} />
              {editing ? "Edit backorder" : "Flag as on backorder"}
            </h3>
          </div>
          <button
            data-testid="backorder-form-close"
            onClick={onClose}
            className="nb-btn bg-white p-2"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-5 grid gap-4 sm:grid-cols-2">
          <label className="sm:col-span-2">
            <div className="label-eyebrow mb-1">Stock item *</div>
            <select
              data-testid="backorder-stock-select"
              className="nb-input"
              value={form.stock_id}
              disabled={!!lockedStockId || !!editing}
              onChange={(e) => pickStock(e.target.value)}
            >
              <option value="">— Pick an item —</option>
              {stocks.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            <div className="label-eyebrow mb-1">Qty expected *</div>
            <input
              data-testid="backorder-qty-input"
              type="number"
              min="0"
              step="any"
              className="nb-input font-mono-num"
              value={form.qty_expected}
              onChange={(e) => set("qty_expected", e.target.value)}
            />
          </label>

          <label>
            <div className="label-eyebrow mb-1">Expected ETA</div>
            <input
              data-testid="backorder-eta-input"
              type="date"
              className="nb-input"
              value={form.eta || ""}
              onChange={(e) => set("eta", e.target.value)}
            />
          </label>

          <label>
            <div className="label-eyebrow mb-1">Supplier</div>
            <select
              data-testid="backorder-supplier-select"
              className="nb-input"
              value={form.supplier_id || ""}
              onChange={(e) => set("supplier_id", e.target.value)}
            >
              <option value="">— None —</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            <div className="label-eyebrow mb-1">PO ref / order ref</div>
            <input
              data-testid="backorder-poref-input"
              className="nb-input"
              value={form.po_ref || ""}
              onChange={(e) => set("po_ref", e.target.value)}
              placeholder="e.g. PO-2026-014, phone order Mar 3"
            />
          </label>

          <label className="sm:col-span-2">
            <div className="label-eyebrow mb-1">Notes</div>
            <textarea
              data-testid="backorder-notes-input"
              className="nb-input min-h-[60px]"
              value={form.notes || ""}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Supplier confirmation details, partial-fulfilment details, etc."
            />
          </label>
        </div>

        <div className="sticky bottom-0 bg-white px-5 py-4 border-t-2 border-black flex justify-end gap-2">
          <button onClick={onClose} className="nb-btn bg-white px-4 py-2">
            Cancel
          </button>
          <button
            data-testid="backorder-form-save"
            onClick={submit}
            disabled={busy || !form.stock_id || !form.qty_expected}
            className="nb-btn nb-btn-primary px-4 py-2 flex items-center gap-1"
          >
            <Save size={14} strokeWidth={2.5} /> Save
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BackorderFormDialog;
